import {Dispatch, FC, SetStateAction, useState} from 'react'
import {RequestDataType} from '../tabs/RequestsTab'
import {ResourceBadge} from './ResourceBadge'
import {DecisionMakerDisplay} from './DecisionMakerDisplay'
import {StatusBadge} from './StatusBadge'
import {format} from 'date-fns'
import ConfirmationModal from 'app/components/ConfirmationModal'
import {useAuth} from 'app/modules/auth'
import {Timestamp, updateDoc, doc} from '@firebase/firestore'
import {showSuccessToast} from 'app/components/ShowToast'
import db from 'services/firebase'

type Props = {
  request: RequestDataType
  index: number
  activeRowId: string
  allSelected: boolean
  checkedRequests: string[]
  handleCheckChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  showAllResources: boolean
  setShowAllResources: Dispatch<SetStateAction<boolean>>
}

const RequestTableRow: FC<Props> = ({
  request,
  index,
  allSelected,
  checkedRequests,
  activeRowId,
  handleCheckChange,
  showAllResources,
  setShowAllResources,
}) => {
  const {currentUser} = useAuth()
  const [loading, setLoading] = useState(false)
  const [isStatusUpdateModalOpen, setIsStatusUpdateModalOpen] = useState(false)
  const [reqStatus, setReqStatus] = useState<'Approved' | 'Rejected'>('Rejected')

  const updateRequestStatus = async () => {
    try {
      setLoading(true)
      const tenantId = currentUser?.tenantId || ''
      const requestDocRef = doc(db, 'tenants', tenantId, 'requests_access', request.id)

      await updateDoc(requestDocRef, {
        status: reqStatus,
        decisionMadeBy: {
          id: currentUser?.uid,
          name: currentUser?.userName,
          date: Timestamp.fromDate(new Date()),
        },
      })

      showSuccessToast({message: 'Status for requested resource has been updated successfully'})
    } catch (error) {
      console.error(`Error while updating status for request`, error)
    } finally {
      setIsStatusUpdateModalOpen(false)
      setLoading(false)
    }
  }

  return (
    <>
      <tr
        key={request.id}
        style={{
          height: '60px',
        }}
        className={`${activeRowId === request.id ? 'bg-gray-100' : ''}`}
      >
        {/* <td>
        <div className='form-check form-check-sm form-check-custom form-check-solid'>
          <input
            className='form-check-input widget-9-check'
            type='checkbox'
            value={request.id}
            checked={allSelected || checkedRequests.includes(request.id)}
            onChange={handleCheckChange}
          />
        </div>
      </td> */}

        <td>
          <h4 className='text-dark fw-bold fs-4 mb-1'>
            {request.firstname}&nbsp;{request.lastname}
          </h4>
          <p className='text-secondary-gray fw-medium fs-7 mb-1'>{request.email}</p>
          <p className='text-secondary-gray fw-medium fs-7 mb-0'>{request.company}</p>
        </td>

        <td className='text-dark fw-medium fs-6'>
          {format(request.requestedDate.toDate(), 'd MMMM, yyyy')}
        </td>

        <td>
          <div className='d-flex flex-column'>
            <div className='d-flex flex-stack'>
              <span className='fs-7 py-1 fw-semibold'>
                <ResourceBadge
                  resources={request.resources}
                  showAll={showAllResources}
                  toggleShow={() => setShowAllResources(true)}
                />
              </span>
            </div>
          </div>
        </td>

        <td>
          <StatusBadge status={request.status} />
        </td>

        <td>
          <DecisionMakerDisplay decisionMaker={request.decisionMadeBy?.name ?? ''} />
        </td>

        <td className=''>
          <div className='dropdown mx-2' style={{position: 'relative'}}>
            <button
              className='btn btn-secondary border-0 p-0 m-2'
              type='button'
              id='dropdownMenu2'
              data-bs-toggle='dropdown'
              aria-haspopup='true'
              aria-expanded='false'
              disabled={request.status !== 'Needs Decision'}
            >
              <img src='/media/buttons/Options.svg' alt='Options' onClick={() => {}} />
            </button>

            {request.status === 'Needs Decision' && (
              <ul className='dropdown-menu' aria-labelledby='dropdownMenu2'>
                <li>
                  <button
                    className='dropdown-item text-hover-primary d-flex align-items-center'
                    style={{color: '#5A5A5A'}}
                    type='button'
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      // updateRequestStatus({id: request.id, status: 'Approved'})
                      setReqStatus('Approved')
                      setIsStatusUpdateModalOpen(true)
                    }}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='18'
                      height='18'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      className='icon icon-tabler icons-tabler-outline mx-1'
                    >
                      <path d='M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0' />
                      <path d='M9 12l2 2l4 -4' />
                    </svg>
                    <span>Approve</span>
                  </button>
                </li>

                <li>
                  <button
                    className='dropdown-item text-hover-danger d-flex align-items-center'
                    style={{color: '#5A5A5A'}}
                    type='button'
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      // updateRequestStatus({id: request.id, status: 'Rejected'})
                      setReqStatus('Rejected')
                      setIsStatusUpdateModalOpen(true)
                    }}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/'
                      width='18'
                      height='18'
                      viewBox='0 -960 960 960'
                      fill='currentColor'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      className='icon icon-tabler icons-tabler-outline mx-1'
                    >
                      <path
                        d='m339-288 141-141 141 141 51-51-141-141 141-141-51-51-141 141-141-141-51 51 141 141-141 141
                       51 51ZM480-96q-79 0-149-30t-122.5-82.5Q156-261 126-331T96-480q0-80 30-149.5t82.5-122Q261-804 331-834t149-30q80 0
                        149.5 30t122 82.5Q804-699 834-629.5T864-480q0 79-30 149t-82.5 122.5Q699-156 629.5-126T480-96Zm0-72q130 0 221-91
                        t91-221q0-130-91-221t-221-91q-130 0-221 91t-91 221q0 130 91 221t221 91Zm0-312Z'
                      />
                    </svg>
                    <span>Reject</span>
                  </button>
                </li>
              </ul>
            )}
          </div>
        </td>
      </tr>

      {isStatusUpdateModalOpen && (
        <ConfirmationModal
          type='default'
          isModalOpen={isStatusUpdateModalOpen}
          hideModal={() => setIsStatusUpdateModalOpen(false)}
          confirmBtnClickHandler={updateRequestStatus}
          confirmButtonText='Update'
          message='This will update the status of resource access request and cannot be undone.'
          isLoading={loading}
        />
      )}
    </>
  )
}

export default RequestTableRow
